import React, { useState } from 'react';
import {
  RolePreference,
  LearningStyle,
  KnowledgeLevel,
  KnowledgeDomain,
  ROLE_LABELS,
  ROLE_DESCRIPTIONS,
  LEARNING_STYLE_LABELS,
  LEARNING_STYLE_DESCRIPTIONS,
  KNOWLEDGE_LEVEL_LABELS,
  DOMAIN_LABELS,
  SelfTestResults,
} from './testDefinitions';
import { TipIcon } from '../../../icons';

interface SelfTestsProps {
  onComplete: (results: SelfTestResults) => void;
  onCancel: () => void;
}

/**
 * SelfTests
 * 
 * Eenvoudige zelftests als data-input.
 * Geen feedback, geen conclusies, geen labels.
 */
const SelfTests: React.FC<SelfTestsProps> = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState<'role' | 'learning' | 'knowledge'>('role');
  const [rolePreference, setRolePreference] = useState<RolePreference | undefined>();
  const [learningStyles, setLearningStyles] = useState<LearningStyle[]>([]);
  const [knowledgeLevels, setKnowledgeLevels] = useState<Record<KnowledgeDomain, KnowledgeLevel>>({
    strategy: 'new',
    marketing: 'new',
    sales: 'new',
    product: 'new',
    operations: 'new',
    finance: 'new',
    team: 'new',
  });

  const roles: RolePreference[] = ['doer', 'thinker', 'connector', 'leader'];
  const availableLearningStyles: LearningStyle[] = ['visual', 'auditory', 'reading', 'kinesthetic'];
  const domains: KnowledgeDomain[] = ['strategy', 'marketing', 'sales', 'product', 'operations', 'finance', 'team'];
  const levels: KnowledgeLevel[] = ['new', 'learning', 'familiar', 'experienced'];

  const handleRoleNext = () => {
    if (rolePreference) {
      setStep('learning');
    }
  };

  const handleLearningNext = () => {
    if (learningStyles.length > 0) {
      setStep('knowledge');
    }
  };

  const toggleLearningStyle = (style: LearningStyle) => {
    setLearningStyles((prev) => {
      if (prev.includes(style)) {
        return prev.filter((s) => s !== style);
      } else {
        return [...prev, style];
      }
    });
  };

  const handleComplete = () => {
    const results: SelfTestResults = {
      rolePreference,
      learningStyles, // Array of selected styles
      learningStyle: learningStyles[0], // Backward compatibility - use first selected style
      knowledgeLevels,
      completedAt: Date.now(),
    };
    onComplete(results);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90dvh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {step === 'role' ? 'Rolvoorkeur' : step === 'learning' ? 'Leerstijl' : 'Kennisniveau per domein'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {step === 'role'
              ? 'Welke rol past het beste bij hoe je graag werkt?'
              : step === 'learning'
              ? 'Hoe leer je nieuwe dingen het liefst?'
              : 'Geef per domein aan hoe bekend je ermee bent.'}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {step === 'role' ? (
            <div className="space-y-3">
              {roles.map((role) => (
                <label
                  key={role}
                  className={`
                    block p-4 border-2 rounded-lg cursor-pointer transition-all
                    ${
                      rolePreference === role
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role}
                    checked={rolePreference === role}
                    onChange={() => setRolePreference(role)}
                    className="sr-only"
                  />
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <div
                        className={`
                          w-4 h-4 rounded-full border-2 flex items-center justify-center
                          ${
                            rolePreference === role
                              ? 'border-gray-900 bg-gray-900'
                              : 'border-gray-300'
                          }
                        `}
                      >
                        {rolePreference === role && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{ROLE_LABELS[role]}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {ROLE_DESCRIPTIONS[role]}
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          ) : step === 'learning' ? (
            <div className="space-y-3">
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  <TipIcon size={14} className="inline" /> <strong>Waarom vragen we dit?</strong> We passen de leer-opdrachten aan op basis van jouw voorkeur. Je kunt meerdere stijlen selecteren.
                </p>
              </div>
              {availableLearningStyles.map((style) => {
                const isSelected = learningStyles.includes(style);
                return (
                  <label
                    key={style}
                    className={`
                      block p-4 border-2 rounded-lg cursor-pointer transition-all
                      ${
                        isSelected
                          ? 'border-gray-900 bg-gray-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      name="learning"
                      value={style}
                      checked={isSelected}
                      onChange={() => toggleLearningStyle(style)}
                      className="sr-only"
                    />
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <div
                          className={`
                            w-4 h-4 rounded border-2 flex items-center justify-center
                            ${
                              isSelected
                                ? 'border-gray-900 bg-gray-900'
                                : 'border-gray-300'
                            }
                          `}
                        >
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {LEARNING_STYLE_LABELS[style]}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          {LEARNING_STYLE_DESCRIPTIONS[style]}
                        </p>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          ) : (
            <div className="space-y-6">
              {domains.map((domain) => (
                <div key={domain} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-900">
                    {DOMAIN_LABELS[domain]}
                  </label>
                  <div className="flex space-x-2">
                    {levels.map((level) => (
                      <button
                        key={level}
                        onClick={() =>
                          setKnowledgeLevels((prev) => ({
                            ...prev,
                            [domain]: level,
                          }))
                        }
                        className={`
                          flex-1 px-3 py-2 text-xs rounded-lg border-2 transition-all
                          ${
                            knowledgeLevels[domain] === level
                              ? 'border-gray-900 bg-gray-50 text-gray-900 font-medium'
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                          }
                        `}
                      >
                        {KNOWLEDGE_LEVEL_LABELS[level]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={
              step === 'role' 
                ? onCancel 
                : step === 'learning' 
                ? () => setStep('role') 
                : () => setStep('learning')
            }
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            {step === 'role' ? 'Annuleren' : 'Vorige'}
          </button>
          <button
            onClick={
              step === 'role' 
                ? handleRoleNext 
                : step === 'learning' 
                ? handleLearningNext 
                : handleComplete
            }
            disabled={
              (step === 'role' && !rolePreference) || 
              (step === 'learning' && learningStyles.length === 0)
            }
            className="
              px-6 py-2 bg-gray-900 text-white rounded-lg
              hover:bg-gray-800 transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            {step === 'knowledge' ? 'Voltooien' : 'Volgende'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelfTests;
